export const POLLING_INTERVAL_MS = 10_000;
export const HEALTH_POLL_MS = 30_000;
export const UPLOAD_THROTTLE_MS = 1_500;

// ─── Category & Reason Code Constants ─────────────────
export const CATEGORY_KEYS = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4', 'KU'] as const;
// Why: KU/Unklassifiziert is display-only — forcing to KU is nonsensical
export const ASSIGNABLE_CATEGORIES = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4'] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  K0: 'Standard-Prozess',
  K1: 'Standard-Info',
  K2: 'Technische Anfrage',
  K3A: 'Beschwerde',
  K3B: 'Eskalation',
  K4: 'Gefahr im Verzug',
  KU: 'Unklassifiziert',
  // WIM triage role: primaryAxis = handling id, secondaryAxis[0] = process_type id.
  // NO surface reads this map directly any more — TriagePanel's carve-out (a direct
  // `CATEGORY_LABELS[h] ?? h`) was removed once a tenant could re-word its own vocabulary, because
  // it made the panel the only place a rename could NOT reach; panel and row would have disagreed
  // on one screen. Every consumer now goes through categoryMeta.resolveLabel(), which reaches this
  // tier ONLY for a role that OWNS the legacy K-vocabulary (gMSB/wMSB). A wim ticket with no
  // vocabulary loaded therefore gets the placeholder floor rather than borrowing these words —
  // see categoryMeta.ts's role gate. These H-entries survive as the backstop the wim vocabulary
  // overrides, and as themeContrast's coherence-pin counterpart, not as a rendering path.
  // Ids are collision-free across roles (K* vs H* vs slugs).
  H1: 'Weiterleiten',
  H2: 'WIM-intern',
  H3: 'Ablegen',
  H4: 'Prüfen',
  anmeldung_msb_wechsel: 'An-/Abmeldung, MSB-Wechsel',
  stammdaten_sdae: 'Stammdaten / SDÄ',
  geraetewechsel: 'Gerätewechsel',
  messwerte: 'Messwerte',
  lastgang_bilanzierung: 'Lastgang / Bilanzierung',
  marktnachricht_fehler: 'Marktnachricht-Fehler',
  einspeisung_eeg: 'Einspeisung / EEG',
  messkonzept_wandler: 'Messkonzept / Wandler',
  kundenbeschwerde: 'Kundenbeschwerde',
  zustaendigkeitsklaerung: 'Zuständigkeitsklärung',
  quotes: 'QUOTES',
  sonstiges: 'Sonstiges',
};

// WIM handling ids by MEANING — the single FE enumeration of H1–H4. Comparisons key here
// (forward-eligibility etc.), never to a raw 'H1' at the call site; theme.wimHandling and
// CATEGORY_LABELS' H-entries are keyed against this set by the coherence pin in themeContrast.
export const WIM_HANDLING = { forward: 'H1', wimIntern: 'H2', archive: 'H3', review: 'H4' } as const;
export type WimHandlingId = typeof WIM_HANDLING[keyof typeof WIM_HANDLING];
export const WIM_HANDLING_IDS: readonly WimHandlingId[] = Object.values(WIM_HANDLING);

// FE mirror of core MsbKCatalog.UnclassifiedId: the one key the categorization-performance
// views exclude (KU rides total-reconciliation views only).
export const UNCLASSIFIED_KEY = 'KU';

// Why: EDIFACT signal atoms only exist for K0/K1/K2 (tools/signals/nb/edifact/).
// Without this gate the BE 400s on K3A/K3B/K4 + EDIFACT with "no atoms found".
export const EDIFACT_CATEGORIES = ['K0', 'K1', 'K2'];
export const EDIFACT_FALLBACK_CATEGORY = 'K2';
export const TESTGEN_DEFAULT_TARGET_CATEGORY = 'K2';

export const REASON_CODES = [
  { code: 'termin_status', label: 'Reine Termin-/Statusanfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'standard_prozess', label: 'Standard-Prozess (PIN/Baustrom/Ablesung/HW-Defekt)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'kontaktformular', label: 'Kontaktformular / Allgemeine Anfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'nicht_zustaendig', label: 'Nicht zuständig / Falsche Adresse', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'irrlaeufer_intern', label: 'Irrläufer (intern)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'allgemeine_info', label: 'Allgemeine Information (Preise/Datenschutz/iMSys)', minCategory: 'K1', maxCategory: 'K1' },
  { code: 'technische_bewertung', label: 'Technische Bewertung nötig (TAB/RFZ/Klassenzuordnung)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'systemrecherche', label: 'Systemrecherche / Zählernummer / MaLo-ID', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'koordination_drittpartei', label: 'Koordination mit Installateur / Drittpartei', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'leistungsaenderung', label: 'Leistungsänderung / Wallbox / PV / Einspeisung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'eigentuemerwechsel', label: 'Eigentümerwechsel / Umzug / Todesfall', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'datenschutz', label: 'Datenschutz / DSGVO / Datenzugriff', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'steuerung_14a', label: 'Steuerung §14a EnWG', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'anhang_verwiesen', label: 'Anhang verwiesen (Inhalt nicht sichtbar)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'msb_wechsel', label: 'MSB-Wechsel (§5 MsbG)', minCategory: 'K1', maxCategory: 'K3A' },
  { code: 'messstreitigkeit', label: 'Messstreitigkeit / Verbrauchsabweichung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'abrechnungsstreit', label: 'Abrechnungsstreit / Entgeltforderung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'rueckrufbitte', label: 'Rückrufbitte', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'vollmacht_vertretung', label: 'Vollmacht / Vertretung / Hausverwaltung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'mehrere_anliegen', label: 'Mehrere Anliegen gemischt', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'beschwerde', label: 'Beschwerde / Frust (ohne rechtliche Drohung)', minCategory: 'K3A', maxCategory: 'K3A' },
  { code: 'eskalation', label: 'Eskalation: Drohung / Anwalt / Frist / GF-Bezug', minCategory: 'K3B', maxCategory: 'K3B' },
  { code: 'akute_gefahr', label: 'Akute Gefahr (Gas/Brand/Spannung)', minCategory: 'K4', maxCategory: 'K4' },
  { code: 'sonstiges', label: 'Sonstiges', minCategory: undefined, maxCategory: undefined },
  { code: 'unbestimmt', label: 'Nicht bestimmbar', minCategory: undefined, maxCategory: undefined },
] as const;

type ReasonCodeEntry = typeof REASON_CODES[number];
const REASON_CODE_MAP = new Map<string, ReasonCodeEntry>(REASON_CODES.map(rc => [rc.code, rc]));

export const getReasonCodeLabel = (code: string): string =>
  REASON_CODE_MAP.get(code)?.label ?? code;

/** Display text for a secondary-axis pill: de-slugged and lowercased.
 *  Lowercasing is EXPLICIT rather than inherited from the corpus — every id ships lowercase today,
 *  so relying on that would make the rule and the coincidence indistinguishable, and a future
 *  mixed-case id would silently break the ruling with no test to catch it. */
export const formatSecondaryAxisCode = (code: string): string => code.replace(/_/g, ' ').toLowerCase();

/** Max visible secondary-axis pills before "+N" overflow, per page */
export const SECONDARY_AXIS_MAX_VISIBLE = {
  dashboard: 3,
  uploads: 5,
  responses: 1,
  detail: 3,
  chatbot: 3,
} as const;

// ─── OCR Options ─────────────────────────────────────
export const OCR_OPTIONS = [
  { value: 'auto', label: 'KI' },
  { value: 'force', label: 'OCR' },
  { value: 'skip', label: 'Skip' },
] as const;

// Shared with categoryMeta.ts's resolveCategoriesForSecondaryAxisCode (the served-aware seam entry point,
// D10 addendum 4) — both expand a min/max PAIR the same way, whether it came from a baked
// REASON_CODES row or a served grund_code value's own attributes. ASSIGNABLE_CATEGORIES (not a
// re-declared literal) is the one home for "the ordered K-band a range can slice" — see D9 for the
// KU exclusion reasoning that already made it the right set for this.
export const expandCategoryRange = (min: string, max: string): string[] => {
  const minIdx = ASSIGNABLE_CATEGORIES.indexOf(min as typeof ASSIGNABLE_CATEGORIES[number]);
  const maxIdx = ASSIGNABLE_CATEGORIES.indexOf(max as typeof ASSIGNABLE_CATEGORIES[number]);
  if (minIdx < 0 || maxIdx < 0) return [];
  return ASSIGNABLE_CATEGORIES.slice(minIdx, maxIdx + 1) as string[];
};

// THE FLOOR TIER for grund-code -> category-band mapping. categoryMeta.ts's
// resolveCategoriesForSecondaryAxisCode is the served-aware entry point most call sites should use — this
// stays the pure baked lookup it consults when a vocabulary serves no row for the code (or serves
// none at all, the pre-vocabulary-delivery state every MSB tenant is in today).
export const getCategoriesForReasonCode = (code: string): string[] => {
  const rc = REASON_CODE_MAP.get(code);
  if (!rc?.minCategory || !rc?.maxCategory) return [];
  return expandCategoryRange(rc.minCategory, rc.maxCategory);
};

// ─── Feature Flags ───────────────────────────────────
export const SHOW_EINGEREICHT_IN_AS = false;

// ─── Status Group Constants ───────────────────────────
export const STATUS_GROUPS = {
  eingereicht: ['submitted', 'ingested'] as string[],
  genehmigt: ['approved', 'sent'] as string[],
} as const;

// "Offen" = everything not in eingereicht or genehmigt

// ─── Confidence Labels ───────────────────────────────
// Per locked Westnetz taxonomy (spec §3 + feedback_westnetz_metric_taxonomy):
//   - Kategoriekonfidenz = AI's self-reported confidence in its category prediction — rendered
//     dynamically via cm.categoryNounCompound + 'konfidenz' (TicketDetail.tsx), never from a
//     fixed label here; a `primaryAxis` entry once lived on this object with zero readers
//     (already dead at Phase 4B's base, renamed-not-removed by task F2) and was deleted outright
//     in the final-review fix wave rather than carried forward under its new name.
//   - Antwortsicherheit  = AI's self-reported confidence in its suggested answer
//     (DO NOT use for empirical approval rate — that's "Abnahmequote", a separate metric)
export const CONFIDENCE_LABELS = {
  suggestion: 'Antwortsicherheit',
} as const;

// ─── Personal Data Field Labels ─────────────────────

type ApiFieldKey =
  | 'emails'
  | 'phoneNumbers'
  | 'ibans'
  | 'dates'
  | 'postalCodes'
  | 'names';

// Keys match API JSON field casing (camelCase).
export const PERSONAL_DATA_LABELS: Record<ApiFieldKey, string> = {
  emails: 'E-Mail-Adressen',
  phoneNumbers: 'Telefonnummern',
  ibans: 'IBANs',
  dates: 'Datumsangaben',
  postalCodes: 'Postleitzahlen',
  names: 'Namen',
};

export const normalizeKey = (key: string): ApiFieldKey =>
  (key[0].toLowerCase() + key.slice(1)) as ApiFieldKey;

// ─── Status Labels (German UI) ──────────────────────
export const STATUS_LABELS: Record<string, string> = {
  submitted: 'Eingereicht',
  ingested: 'Aufgenommen',
  categorizing: 'Kategorisierung',
  categorized: 'Kategorisiert',
  generating_suggestion: 'Vorschlag wird erstellt',
  suggestion_ready: 'Vorschlag bereit',
  approved: 'Genehmigt',
  sent: 'Gesendet',
  rejected: 'Abgelehnt',
  reanalyzing: 'Erneut analysiert',
  pipeline_error: 'Fehler',
  archived: 'Archiviert',
} as const;

export const PIPELINE_STAGE_LABELS: Record<string, string> = {
  parse: 'Parsen',
  pii_extraction: 'Personendaten-Erkennung',
  attachment_extraction: 'Anhang-Extraktion',
  categorization: 'Kategorisierung',
  ocr_execution: 'OCR',
  suggestion_generation: 'Antwortgenerierung',
  draft_generation: 'Entwurfserstellung',
};
